import { useCallback, useEffect, useMemo, useRef, useState } from "react"
import { useReducedMotion } from "motion/react"

import { useAppState } from "../../context/appStateContext"
import native from "../../native/native"
import { formatCurrency } from "../../utils/utils"
import svg from "../../utils/svg"
import styles from "./ShadePaywall.module.scss"

/**
 * ShadePaywall (`/shade`) — an original, self-contained ONBOARDING paywall for
 * Private Photo Vault Premium. Paywall type rolled at random this run: ONBOARDING.
 *
 * ── Style directive (read live from #paywalls each run) ─────────────────────
 * The newest @Paywalls-Bot mention this run is `focus on security and privacy`
 * (NOT the reset keyword `default`), so it is an ACTIVE STANDING DIRECTIVE at
 * super-high priority, and the whole concept is built to it: privacy told as a
 * one-way window — who is allowed to look in at your memories.
 *
 * ── Concept ────────────────────────────────────────────────────────────────
 * "Draw the blinds on your memories." Right now your first photos sit in an open
 * window — anyone who picks up your phone or glances over your shoulder can see
 * straight in. Private Photo Vault hangs a ONE-WAY PRIVACY SHADE over that
 * window: draw it down and, from the outside, the memories vanish behind a warm
 * private blind — while YOU keep every photo and open them anytime with Face ID.
 * The onboarding truth told as the small, satisfying act of drawing the blinds
 * at dusk: nothing is moved or deleted; you simply decide who gets to see in.
 *
 * ── Interaction (the hero mechanic) ─────────────────────────────────────────
 * A domestic mullioned WINDOW holds a 3×3 view of your real first memories, a
 * live "who can see in" status above the sill. You GRAB THE PULL-RING and DRAW
 * THE SHADE DOWN over the glass — a vertical pull-down of a roller blind. As it
 * descends the room warms to dusk, the four real safeguards ignite one-by-one,
 * and past the sill it snaps home with a brass latch: the status flips
 * "Anyone can see in → Only you can see in." A DRAW-DOWN-A-BLIND gesture — new
 * to the set: not a horizontal ink-roller (Watermark), a pattern trace (Trace),
 * a keypad (Only You), a fingerprint press (Imprint), a boot switch (Firstlight),
 * a snow-globe shake (Flurry) or a rhythmic tap (Vigil). Replayable; auto-demos
 * the draw once, a tap raises it, and dragging re-draws it.
 *
 * ── Divergence (vs. the 140+ prior concepts, incl. the security cluster) ────
 * Layout      : a domestic WINDOW-FRAME hero with a valance/roller shade on top
 *               and a warm sill, a "who can see in" readout above and a dusk
 *               "lock the room" pricing card below — not a hero-on-top /
 *               feature-bullets / CTA stack, not a console, keypad or certificate.
 * Interaction : DRAW-THE-SHADE — a vertical pull-down of a one-way blind. New
 *               gesture for the set.
 * Metaphor    : DRAWING THE BLINDS on a lit window — the oldest, warmest privacy
 *               gesture there is. The window full of photos keeps it unmistakably
 *               a photo vault.
 * Value frame : CONTROL over who is allowed to LOOK IN — contrast framing (open
 *               window vs. drawn shade), calm and domestic, not fear.
 * Register    : warm, dusk-lit, homey — a room settling into privacy for the
 *               night. Calm-trust, quietly satisfying.
 * CTA         : "Draw the blinds" — yearly featured.
 *
 * ── Honest & App Store compliant ───────────────────────────────────────────
 * Every safeguard named is a REAL Private Photo Vault feature (Face ID / Pattern
 * lock · encrypted Cloud Vault · break-in reports · app disguise). No countdown,
 * no fabricated scarcity, NO data-loss threat — the shade only decides who is
 * ALLOWED to see your photos; nothing is moved and nothing is deleted, and you
 * keep every memory. "Save 79%" is exact arithmetic from live product data.
 * Yearly is the featured, default, most prominent price; monthly is small and
 * secondary. The 7-day free trial is the real trial. Renders on built-in debug
 * product data with bundled photos — no native injection, no external egress.
 */

// The memories behind the window — bundled, self-contained repo photos.
const MEMORIES = [
  "/byheart/m02.jpg",
  "/byheart/m05.jpg",
  "/byheart/m09.jpg",
  "/byheart/m14.jpg",
  "/byheart/m03.jpg",
  "/byheart/m11.jpg",
  "/byheart/m17.jpg",
  "/byheart/m08.jpg",
  "/byheart/m12.jpg",
]

// Real Private Photo Vault safeguards that light as the shade is drawn. Each
// carries the fraction of the draw at which it ignites (top pane → bottom rail).
const GUARDS = [
  {
    key: "lock",
    at: 0.16,
    Icon: svg.featureIcons.PatternLock,
    name: "Face ID & pattern lock",
    line: "Only you can open the window",
  },
  {
    key: "cloud",
    at: 0.38,
    Icon: svg.featureIcons.CloudVault,
    name: "Encrypted Cloud Vault",
    line: "A private backup, yours on every device",
  },
  {
    key: "breakin",
    at: 0.6,
    Icon: svg.featureIcons.Breakin,
    name: "Break-in reports",
    line: "A silent photo of anyone who tries",
  },
  {
    key: "disguise",
    at: 0.82,
    Icon: svg.featureIcons.Disguise,
    name: "App disguise",
    line: "Hide the whole vault behind an ordinary app",
  },
]

const SEAL_AT = 0.62 // draw fraction past which the shade latches home

const ShadePaywall = () => {
  const { state } = useAppState()
  const reduceMotion = useReducedMotion()
  const darkMode = state.initialData?.darkMode ?? false

  const premium = state.productData?.premium
  const yearlyProduct = premium?.yearlyProduct
  const monthlyProduct = premium?.monthlyProduct
  const locale = yearlyProduct?.priceLocale

  const yearlyPrice = yearlyProduct
    ? formatCurrency(yearlyProduct.price, locale)
    : "$35.99"
  const yearlyPerMonth = yearlyProduct
    ? formatCurrency(yearlyProduct.price / 12, locale)
    : "$3.00"
  const monthlyPrice = monthlyProduct
    ? formatCurrency(monthlyProduct.price, locale)
    : "$13.99"
  const trialDays = yearlyProduct?.introPricing?.period ?? 7

  const savingsPct = useMemo(() => {
    if (!yearlyProduct || !monthlyProduct) return 79
    const p = Math.round(
      (1 - yearlyProduct.price / (monthlyProduct.price * 12)) * 100,
    )
    return p > 0 ? p : 0
  }, [yearlyProduct, monthlyProduct])

  const [plan, setPlan] = useState<"yearly" | "monthly">("yearly")

  // ── shade state ───────────────────────────────────────────────────────────
  // d : 0 = window open (raised), 1 = shade fully drawn (latched). A spring
  //     chases a target once the user lets go; while dragging, d tracks the
  //     pointer directly.
  const [d, setD] = useState(0)
  const dRef = useRef(0)
  const targetRef = useRef(0)
  const raf = useRef<number | null>(null)
  const userActed = useRef(false)
  const dragging = useRef(false)
  const winRef = useRef<HTMLDivElement | null>(null)

  const drawn = d > SEAL_AT

  const tick = useCallback(() => {
    const target = targetRef.current
    const cur = dRef.current
    const k = target > cur ? 0.14 : 0.18
    let next = cur + (target - cur) * k
    if (Math.abs(target - next) < 0.0012) next = target
    dRef.current = next
    setD(next)
    if (next !== target) raf.current = requestAnimationFrame(tick)
    else raf.current = null
  }, [])

  const springTo = useCallback(
    (v: number) => {
      targetRef.current = v
      if (reduceMotion) {
        if (raf.current != null) cancelAnimationFrame(raf.current)
        raf.current = null
        dRef.current = v
        setD(v)
        return
      }
      if (raf.current == null) raf.current = requestAnimationFrame(tick)
    },
    [reduceMotion, tick],
  )

  const setImmediate = useCallback((v: number) => {
    if (raf.current != null) {
      cancelAnimationFrame(raf.current)
      raf.current = null
    }
    dRef.current = v
    targetRef.current = v
    setD(v)
  }, [])

  // ── pointer: draw the shade down (or raise it back up) ──────────────────────
  const yToD = useCallback((clientY: number) => {
    const el = winRef.current
    if (!el) return 0
    const r = el.getBoundingClientRect()
    const raw = (clientY - r.top) / r.height
    return Math.max(0, Math.min(1, raw))
  }, [])

  const onDown = useCallback(
    (e: React.PointerEvent) => {
      userActed.current = true
      dragging.current = true
      setImmediate(yToD(e.clientY))
      try {
        ;(e.currentTarget as Element).setPointerCapture?.(e.pointerId)
      } catch {}
    },
    [setImmediate, yToD],
  )

  const onMove = useCallback(
    (e: React.PointerEvent) => {
      if (!dragging.current) return
      setImmediate(yToD(e.clientY))
    },
    [setImmediate, yToD],
  )

  const onUp = useCallback(() => {
    if (!dragging.current) return
    dragging.current = false
    springTo(dRef.current >= SEAL_AT ? 1 : 0)
  }, [springTo])

  // A tap (no real drag) toggles: raise a drawn shade, or draw an open one.
  const toggle = useCallback(() => {
    userActed.current = true
    springTo(dRef.current > SEAL_AT ? 0 : 1)
  }, [springTo])

  // Auto-demo once so the payoff reads; reduced-motion arrives drawn & calm.
  useEffect(() => {
    if (reduceMotion) {
      setImmediate(1)
      return
    }
    if (window.location.search.includes("nodemo")) return
    const t = window.setTimeout(() => {
      if (!userActed.current) springTo(1)
    }, 900)
    return () => {
      window.clearTimeout(t)
      if (raf.current != null) cancelAnimationFrame(raf.current)
      raf.current = null
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reduceMotion])

  const handleSubscribe = useCallback(() => {
    const product = plan === "yearly" ? yearlyProduct : monthlyProduct
    if (product) native.subscribe(product)
    else native.postNativeEvent("subscribe")
  }, [plan, yearlyProduct, monthlyProduct])

  const litCount = GUARDS.filter((g) => d >= g.at).length

  return (
    <div
      className={`${styles.page} ${drawn ? styles.isDrawn : ""}`}
      style={{ ["--d" as any]: d }}
    >
      <div className={styles.scroll}>
        {/* ── header ─────────────────────────────────────────────────────── */}
        <header className={styles.top}>
          <div className={styles.brand}>
            <span className={styles.brandMark} aria-hidden="true">
              <svg viewBox="0 0 24 24" width="15" height="15">
                <rect
                  x="3.5"
                  y="4.5"
                  width="17"
                  height="15"
                  rx="2.4"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="1.7"
                />
                <path
                  d="M3.5 12h17M12 4.5v15"
                  stroke="currentColor"
                  strokeWidth="1.4"
                  opacity="0.65"
                />
              </svg>
            </span>
            <span className={styles.brandName}>
              Private Photo Vault
              <span className={styles.brandTag}>Premium</span>
            </span>
          </div>
          <button
            className={styles.close}
            onClick={() => native.postNativeEvent("close")}
            aria-label="Close"
          >
            <svg viewBox="0 0 24 24" width="15" height="15">
              <path
                d="M6 6l12 12M18 6L6 18"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
              />
            </svg>
          </button>
        </header>

        {/* ── intro ──────────────────────────────────────────────────────── */}
        <section className={styles.intro}>
          <span className={styles.kicker}>
            <span className={styles.kickerDot} aria-hidden="true" />
            Set up Private Photo Vault
          </span>
          <h1 className={styles.title}>
            {drawn ? (
              <>
                Now they open for
                <br />
                <span className={styles.accent}>your eyes only.</span>
              </>
            ) : (
              <>
                Your photos sit in an
                <br />
                <span className={styles.accentWarn}>open window.</span>
              </>
            )}
          </h1>
          <p className={styles.sub}>
            {drawn
              ? "The blinds are drawn. From the outside your memories are gone — inside, every one is still yours, opened only with Face ID."
              : "Anyone who picks up your phone can see straight in. Draw the blinds once and outside eyes lose the view — you keep every photo."}
          </p>
        </section>

        {/* ── the window (hero) ──────────────────────────────────────────── */}
        <section className={styles.stage}>
          <div
            className={`${styles.readout} ${drawn ? styles.readoutSafe : ""}`}
            aria-live="polite"
          >
            <span className={styles.readoutDot} aria-hidden="true" />
            {drawn ? (
              <>
                Now · <b>only you can see in</b>
              </>
            ) : (
              <>
                Right now · <b>anyone can see in</b>
              </>
            )}
          </div>

          <div
            ref={winRef}
            className={styles.window}
            onPointerDown={onDown}
            onPointerMove={onMove}
            onPointerUp={onUp}
            onPointerCancel={onUp}
            role="button"
            tabIndex={0}
            aria-label={
              drawn
                ? "Blinds drawn — only you can see in. Activate to raise them."
                : "Draw the privacy blind down over your photos."
            }
            onKeyDown={(e) => {
              if (e.key === "Enter" || e.key === " ") {
                e.preventDefault()
                toggle()
              }
            }}
          >
            {/* the outside light spilling through the glass */}
            <span className={styles.daylight} aria-hidden="true" />

            {/* the 3×3 view of your memories */}
            <div className={styles.panes} aria-hidden="true">
              {MEMORIES.map((src) => (
                <span
                  key={src}
                  className={styles.pane}
                  style={{ backgroundImage: `url(${src})` }}
                />
              ))}
            </div>

            {/* mullioned frame over the glass */}
            <span className={styles.mullions} aria-hidden="true">
              <i className={styles.mV} style={{ left: "33.333%" }} />
              <i className={styles.mV} style={{ left: "66.666%" }} />
              <i className={styles.mH} style={{ top: "33.333%" }} />
              <i className={styles.mH} style={{ top: "66.666%" }} />
            </span>

            {/* the roller shade drawn down from the valance */}
            <span className={styles.shade} aria-hidden="true">
              <span className={styles.shadeCloth}>
                <span className={styles.shadeGlow} />
                <span className={styles.crest}>
                  <svg viewBox="0 0 24 24" width="26" height="26">
                    <path
                      className={styles.shackle}
                      d="M8 11V8a4 4 0 0 1 8 0v3"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="1.9"
                      strokeLinecap="round"
                    />
                    <rect
                      x="5.6"
                      y="11"
                      width="12.8"
                      height="9.4"
                      rx="2.3"
                      fill="currentColor"
                    />
                  </svg>
                  <span className={styles.crestText}>Private</span>
                </span>
              </span>
              {/* the bottom rail + pull ring the user grabs */}
              <span className={styles.rail}>
                <span className={styles.pull} />
              </span>
            </span>

            {/* the sill */}
            <span className={styles.sill} aria-hidden="true" />

            {/* hint chip */}
            <span
              className={`${styles.hint} ${drawn ? styles.hintDone : ""}`}
              aria-hidden="true"
            >
              <span className={styles.hintArrow}>
                <svg viewBox="0 0 24 24" width="13" height="13">
                  <path
                    d="M12 5v13M6 12l6 6 6-6"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2.2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </span>
              {drawn ? "Blinds drawn" : "Draw the blinds"}
            </span>
          </div>

          {/* the four real safeguards, igniting as the shade descends */}
          <div className={styles.guards}>
            <div className={styles.guardsHead}>
              <span>What comes on with the blinds</span>
              <span className={styles.guardsCount}>
                {litCount}/{GUARDS.length} on
              </span>
            </div>
            <div className={styles.guardRow}>
              {GUARDS.map((g) => {
                const on = d >= g.at
                return (
                  <div
                    key={g.key}
                    className={`${styles.guard} ${on ? styles.guardOn : ""}`}
                  >
                    <span className={styles.guardIcon} aria-hidden="true">
                      <g.Icon width={17} height={17} />
                    </span>
                    <span className={styles.guardName}>{g.name}</span>
                    <span className={styles.guardLine}>{g.line}</span>
                  </div>
                )
              })}
            </div>
          </div>
        </section>

        {/* ── honest framing ─────────────────────────────────────────────── */}
        <p className={styles.proof}>
          <span className={styles.proofMark} aria-hidden="true">
            <svg viewBox="0 0 24 24" width="13" height="13">
              <circle
                cx="12"
                cy="12"
                r="8.2"
                fill="none"
                stroke="currentColor"
                strokeWidth="1.7"
              />
              <path
                d="M8.5 12.4l2.4 2.4 4.6-5"
                fill="none"
                stroke="currentColor"
                strokeWidth="1.8"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </span>
          The blind is one-way. Every photo stays inside and nothing is deleted
          — you simply decide who is allowed to see in.
        </p>

        {/* ── pricing: yearly featured, monthly small & secondary ────────── */}
        <div className={styles.plansLabel}>Keep the blinds drawn</div>

        <button
          className={`${styles.yearly} ${
            plan === "yearly" ? styles.selected : ""
          }`}
          onClick={() => setPlan("yearly")}
          aria-pressed={plan === "yearly"}
        >
          <span className={styles.yearlyPennant} aria-hidden="true" />
          <span className={styles.yearlyHead}>
            <span className={styles.yearlyName}>
              A full year of Premium
              <span className={styles.trialPill}>
                {trialDays}-day free trial
              </span>
            </span>
            {savingsPct > 0 && (
              <span className={styles.saveTag}>Save {savingsPct}%</span>
            )}
          </span>
          <span className={styles.yearlyPriceRow}>
            <span className={styles.yearlyPrice}>{yearlyPrice}</span>
            <span className={styles.yearlyUnit}>/ year</span>
            <span className={styles.yearlyPerMo}>≈ {yearlyPerMonth}/mo</span>
            <span className={styles.check} aria-hidden="true">
              <svg viewBox="0 0 24 24" width="13" height="13">
                <path
                  d="M5 12.5l4.2 4.2L19 7"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="3"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </span>
          </span>
          <span className={styles.yearlyFoot}>
            Every safeguard, on for a full year
          </span>
        </button>

        <button
          className={`${styles.monthly} ${
            plan === "monthly" ? styles.monthlySel : ""
          }`}
          onClick={() => setPlan("monthly")}
          aria-pressed={plan === "monthly"}
        >
          <span>Or pay monthly</span>
          <span className={styles.monthlyPrice}>{monthlyPrice}/mo</span>
        </button>

        <button className={styles.cta} onClick={handleSubscribe}>
          <span className={styles.ctaLabel}>
            {plan === "yearly"
              ? `Draw the blinds — ${trialDays} days free`
              : "Draw the blinds"}
          </span>
          <span className={styles.ctaSub}>
            {plan === "yearly"
              ? `Then ${yearlyPrice}/year · cancel anytime`
              : `${monthlyPrice}/month · cancel anytime`}
          </span>
        </button>

        <div className={styles.footer}>
          <button onClick={() => native.postNativeEvent("already_purchased")}>
            Restore
          </button>
          <span className={styles.dot}>·</span>
          <button onClick={() => native.postNativeEvent("terms")}>Terms</button>
          <span className={styles.dot}>·</span>
          <button onClick={() => native.postNativeEvent("privacy")}>
            Privacy
          </button>
        </div>
      </div>
    </div>
  )
}

export default ShadePaywall
